import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Send, Copy, Check, XCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function TelegramLink() {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState(null);
  const [deepLink, setDeepLink] = useState(null);
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/telegram/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const generate = async () => {
    try {
      const { data } = await api.post("/telegram/link-code");
      setCode(data.code);
      setDeepLink(data.deep_link || null);
      toast.success("Codice generato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unlink = async () => {
    if (!window.confirm("Scollegare il tuo account Telegram?")) return;
    try {
      await api.post("/telegram/unlink");
      toast.success("Account scollegato");
      await refresh();
      fetchStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyCmd = () => {
    if (!code) return;
    navigator.clipboard.writeText(`/collega ${code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-widest font-medium mb-2">
        <Send className="w-4 h-4" /> Telegram
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-2">
        Collega Telegram
      </h1>
      <p className="text-muted-foreground mb-8">
        Ricevi notifiche e prenota direttamente dal bot.
      </p>

      {loading ? (
        <div className="text-muted-foreground">Caricamento…</div>
      ) : !status?.configured ? (
        <div className="bg-card border border-border rounded-lg p-6 text-center" data-testid="telegram-not-configured">
          <XCircle className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium mb-1">Bot non ancora configurato</p>
          <p className="text-sm text-muted-foreground">
            L'amministratore deve configurare il bot Telegram prima che tu possa collegarti.
          </p>
        </div>
      ) : user?.telegram_chat_id ? (
        <div className="bg-card border border-border rounded-lg p-7" data-testid="telegram-linked">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="w-6 h-6 text-primary" />
            <h2 className="font-display text-xl font-medium">Account collegato</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Il tuo profilo Telegram è collegato a Schiscetta. Apri il bot e usa /menu per vedere i piatti di oggi.
          </p>
          <button
            onClick={unlink}
            data-testid="unlink-telegram-button"
            className="px-4 py-2 text-sm border border-accent/30 text-accent rounded-md hover:bg-accent hover:text-white transition-colors"
          >
            Scollega
          </button>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-7 sm:p-8">
          {!code ? (
            <>
              <p className="text-sm text-muted-foreground mb-5">
                Genera un codice usa-e-getta: collegheremo il tuo profilo al bot Telegram
                con un solo tap (oppure puoi inviare il comando manualmente, come preferisci).
              </p>
              <button
                onClick={generate}
                data-testid="generate-code-button"
                className="w-full px-5 py-3 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all"
              >
                Genera codice di collegamento
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="bg-primary/5 border border-primary/20 rounded-md p-5 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Il tuo codice</p>
                <div className="font-display text-4xl font-bold tracking-widest text-primary" data-testid="link-code">
                  {code}
                </div>
                <p className="text-xs text-muted-foreground mt-2">Valido fino al collegamento</p>
              </div>

              {deepLink ? (
                <>
                  <a
                    href={deepLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="open-bot-deeplink"
                    className="w-full px-4 py-3 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" /> Apri il bot e collega in un tap
                    <ExternalLink className="w-3.5 h-3.5 opacity-80" />
                  </a>
                  <p className="text-xs text-muted-foreground text-center">
                    Si apre Telegram con il codice già incluso. Premi <strong>Start</strong> e il collegamento è fatto.
                  </p>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Preferisci farlo manualmente?</summary>
                    <div className="mt-2 space-y-2">
                      <ol className="text-xs text-muted-foreground space-y-1 list-decimal pl-5">
                        <li>Apri il bot su Telegram</li>
                        <li>Invia <code className="px-1 py-0.5 bg-muted rounded">/collega {code}</code></li>
                      </ol>
                      <button
                        onClick={copyCmd}
                        data-testid="copy-command-button"
                        className="w-full px-3 py-2 bg-foreground/90 text-white text-xs rounded-md hover:bg-foreground active:scale-[0.98] transition-all flex items-center justify-center gap-1.5"
                      >
                        {copied ? <><Check className="w-3.5 h-3.5" /> Copiato!</> : <><Copy className="w-3.5 h-3.5" /> Copia comando</>}
                      </button>
                    </div>
                  </details>
                </>
              ) : (
                <button
                  onClick={copyCmd}
                  data-testid="copy-command-button"
                  className="w-full px-4 py-3 bg-foreground text-white font-medium rounded-md hover:bg-foreground/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  {copied ? <><Check className="w-4 h-4" /> Copiato!</> : <><Copy className="w-4 h-4" /> Copia comando completo</>}
                </button>
              )}

              <button
                onClick={generate}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                Rigenera codice
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 text-xs text-muted-foreground">
        Domande? Contatta l'amministratore o consulta la{" "}
        <a href="https://core.telegram.org/bots" target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 hover:underline">
          documentazione Telegram <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
